// Penyimpanan daftar file (file tree) di browser.
//
// Dulu selalu di localStorage, yang batasnya hanya sekitar 5 MB per situs
// (kira-kira 15 ribu file). Akun yang lebih besar (misal backup Dropbox
// ratusan ribu file) gagal disimpan: web tertahan di versi lama, dan
// perubahan dari web bisa tidak terkirim ke bot. Sekarang tree kecil tetap di
// localStorage (langsung tersimpan, seperti dulu), tree besar di IndexedDB
// (batasnya ratusan MB ke atas).

const DB_NAME = 'disbox';
const STORE = 'trees';
// Di atas ini tree disimpan di IndexedDB, supaya localStorage tetap punya ruang
// untuk data kecil lain (status sinkron, upload tertunda, progres tontonan).
export const LOCAL_MAX_CHARS = 1_500_000;

const treeKey = (userId) => `disbox_${userId}`;
const savedAtKey = (userId) => `disbox_${userId}_savedAt`;
const formatKey = (userId) => `disbox_${userId}_store`;

let dbPromise = null;
function openDb() {
    if (!dbPromise) {
        dbPromise = new Promise((resolve, reject) => {
            if (typeof indexedDB === 'undefined') {
                reject(new Error('IndexedDB tidak tersedia di browser ini.'));
                return;
            }
            const request = indexedDB.open(DB_NAME, 1);
            request.onupgradeneeded = () => request.result.createObjectStore(STORE);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
            request.onblocked = () => reject(new Error('IndexedDB sedang dipakai tab lain versi lama.'));
        });
        dbPromise.catch(() => { dbPromise = null; });
    }
    return dbPromise;
}

// Satu transaksi. Transaksi tulis ke store yang sama dijalankan berurutan
// sesuai urutan dibuat, jadi simpanan terakhir selalu yang terakhir menang.
function transact(mode, action) {
    return openDb().then((db) => new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = action(tx.objectStore(STORE));
        tx.oncomplete = () => resolve(request ? request.result : undefined);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error || new Error('Transaksi IndexedDB dibatalkan.'));
    }));
}

// Apakah tree akun ini sudah pernah disimpan dengan cara baru ini. Belum =
// data lama dari localStorage (bisa tertinggal dari catatan sinkronnya).
export function isLegacyFormat(userId) {
    return localStorage.getItem(formatKey(userId)) !== '2';
}

// { tree, savedAt, base, source: 'local' | 'idb', chars } atau null kalau belum ada.
// `base` hanya ada untuk IndexedDB: versi backup yang menjadi dasar tree itu.
export async function loadTree(userId) {
    let local = null;
    const raw = localStorage.getItem(treeKey(userId));
    if (raw) {
        try {
            local = { tree: JSON.parse(raw), savedAt: Number(localStorage.getItem(savedAtKey(userId))) || 0, source: 'local', chars: raw.length };
        } catch {
            local = null;
        }
    }
    let stored = null;
    try {
        const record = await transact('readonly', (store) => store.get(userId));
        if (record && typeof record.json === 'string') {
            stored = { tree: JSON.parse(record.json), savedAt: record.savedAt || 0, base: record.base || null, source: 'idb', chars: record.json.length };
        }
    } catch (error) {
        console.warn('[Disbox] Daftar file di IndexedDB tidak terbaca:', error);
    }
    if (local && stored) return stored.savedAt > local.savedAt ? stored : local;
    return stored || local;
}

let latestSave = 0;

// Menyimpan tree. Tidak pernah melempar error (kalau semua cara gagal, tree
// tetap ada di memori dan perubahan tetap dikirim ke bot). Mengembalikan
// Promise yang selesai setelah tersimpan: { where: 'local' | 'idb' | null, chars }.
export function saveTree(userId, tree, { base = null, savedAt = Date.now() } = {}) {
    const mySave = ++latestSave;
    let json;
    try {
        json = JSON.stringify(tree);
    } catch (error) {
        console.warn('[Disbox] Daftar file tidak bisa disimpan:', error);
        return Promise.resolve({ where: null, chars: 0 });
    }
    if (json.length <= LOCAL_MAX_CHARS) {
        try {
            localStorage.setItem(treeKey(userId), json);
            localStorage.setItem(savedAtKey(userId), String(savedAt));
            localStorage.setItem(formatKey(userId), '2');
            // Salinan besar lama di IndexedDB (kalau ada) tidak dipakai lagi.
            transact('readwrite', (store) => store.delete(userId)).catch(() => {});
            return Promise.resolve({ where: 'local', chars: json.length });
        } catch {
            // localStorage penuh: pakai IndexedDB.
        }
    }
    return transact('readwrite', (store) => store.put({ json, base, savedAt }, userId))
        .then(() => {
            // Salinan lama di localStorage dibuang SETELAH yang baru tersimpan
            // (kalau tab ditutup di tengah jalan, salinan lama masih ada).
            if (mySave === latestSave) {
                localStorage.removeItem(treeKey(userId));
                try {
                    localStorage.setItem(savedAtKey(userId), String(savedAt));
                    localStorage.setItem(formatKey(userId), '2');
                } catch { /* data kecil: mestinya selalu muat */ }
            }
            return { where: 'idb', chars: json.length };
        })
        .catch((error) => {
            console.warn('[Disbox] Daftar file terlalu besar untuk disimpan di browser ini (hanya di memori):', error);
            return { where: null, chars: json.length };
        });
}
